import { useEffect, useRef } from 'react';

/**
 * Re-runs `callback` whenever the browser tab regains focus AND on a periodic
 * interval (default: every 60 seconds) while the tab is visible.
 *
 * Use this on dashboard/compliance pages so that alerts stay in sync with
 * backend data without the user needing to manually refresh the page.
 *
 * @param {Function} callback  - Async or sync refetch function
 * @param {number}   intervalMs - Polling interval in ms (default 60_000)
 */
const useAutoRefresh = (callback, intervalMs = 60000) => {
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    const run = () => {
      try { cbRef.current && cbRef.current(); } catch (_) { /* ignore */ }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') run();
    };
    const onFocus = () => run();

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    const id = setInterval(() => {
      if (document.visibilityState === 'visible') run();
    }, intervalMs);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(id);
    };
  }, [intervalMs]);
};

export default useAutoRefresh;
